import { LITERAL_SOURCE_PREFIX } from './content.js';
import { applyTransformChain } from './transforms/apply-transform-chain.js';
import { normalizeTransforms } from './transforms/normalize-transforms.js';
import { resolveFormatSpecValue } from './format-spec-resolver.js';
import { normalizeMappingOverride } from './column-format-spec.js';
/**
 * Preview-side counterpart to the runtime `resolveColumnValue` in
 * `apps/api/src/services/export/format-generators/column-value-resolver.ts`.
 *
 * When an effective format spec is present (from `mapping.formatOverride` or a
 * column-level `columnSpec`), `resolveFormatSpecValue` is called unconditionally
 * so preview output matches the worker. Non-matching shapes (e.g. a non-numeric
 * value against a number spec) return '' — same as the runtime.
 *
 * When no spec is present, the raw value passes through unchanged.
 *
 * Legacy flat `dateFormat`/`numberFormat` on the mapping were removed from the
 * typed contract (Task 16). `normalizeMappingOverride` still folds them when
 * reading pre-migration JSON shapes from the DB, so older persisted data is
 * handled transparently.
 */
export function resolveMappingValueForPreview(mapping, raw, columnSpec) {
    const effective = normalizeMappingOverride(mapping) ?? columnSpec;
    if (effective) {
        // Worker parity: resolveFormatSpecValue is called UNCONDITIONALLY when an
        // effective spec is present. The resolver itself handles non-matching shapes
        // (e.g. a non-numeric value against a number spec → '') — mirrors the runtime
        // column-value-resolver. Do NOT add value-shape guards here; they diverge
        // preview from worker output.
        return resolveFormatSpecValue(effective, raw);
    }
    return raw;
}
// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------
/**
 * Resolve a source field value from a record or the special system fields map.
 * - `__literal:<text>` resolves to the static literal text.
 * - `_<systemField>` resolves from `special`.
 * - Anything else resolves from `record`.
 */
function resolveValue(sourceField, record, special) {
    if (sourceField.startsWith(LITERAL_SOURCE_PREFIX)) {
        return sourceField.slice(LITERAL_SOURCE_PREFIX.length);
    }
    if (sourceField.startsWith('_')) {
        return special[sourceField] ?? '';
    }
    return record[sourceField] ?? '';
}
/** Build a flat mapped object from one record using the mappings list. */
function mapRecord(mappings, record, special, specByColumn) {
    const out = {};
    const allFieldValues = { ...special, ...record };
    for (const m of mappings) {
        const raw = resolveValue(m.sourceField, record, special);
        // Preview is sync and cannot resolve named chains. Inline steps run; named
        // mode passes through raw (same observable behavior as before the schema
        // was widened, when union shapes silently fell through .length === undefined).
        const normalized = normalizeTransforms(m.transforms);
        let value;
        if (normalized.kind === 'inline' && normalized.steps.length > 0) {
            ({ value } = applyTransformChain(raw, normalized.steps, { allFieldValues }));
        }
        else {
            value = raw;
        }
        // Per-mapping-row format overrides apply AFTER transforms — mirrors the
        // runtime, where applyMappings runs the chain first and the format
        // generators then resolveColumnValue on the post-transform payload.
        out[m.targetColumn] = resolveMappingValueForPreview(m, value, specByColumn?.get(m.targetColumn));
    }
    return out;
}
// ---------------------------------------------------------------------------
// Structured JSON rendering
// ---------------------------------------------------------------------------
function renderStructureNode(node, records, special) {
    switch (node.type) {
        case 'field': {
            // Use first record for scalar field values at group level
            const record = records[0] ?? {};
            return resolveValue(node.sourceField ?? '', record, special);
        }
        case 'group': {
            const obj = {};
            for (const child of node.children ?? []) {
                obj[child.name] = renderStructureNode(child, records, special);
            }
            return obj;
        }
        case 'collection': {
            // Each record becomes one entry in the array
            return records.map((record) => {
                const entry = {};
                for (const child of node.children ?? []) {
                    entry[child.name] = renderStructureNodeForRecord(child, record, special);
                }
                return entry;
            });
        }
    }
}
/** Like renderStructureNode but scoped to a single record (used inside collections). */
function renderStructureNodeForRecord(node, record, special) {
    switch (node.type) {
        case 'field': {
            return resolveValue(node.sourceField ?? '', record, special);
        }
        case 'group': {
            const obj = {};
            for (const child of node.children ?? []) {
                obj[child.name] = renderStructureNodeForRecord(child, record, special);
            }
            return obj;
        }
        case 'collection': {
            // Nested collections repeat the same records (edge case — flatten for preview)
            return [renderStructureNodeForRecord({ ...node, type: 'group' }, record, special)];
        }
    }
}
// ---------------------------------------------------------------------------
// XML helpers
// ---------------------------------------------------------------------------
function escapeXml(val) {
    return String(val)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}
function renderXmlValue(tag, val) {
    return `<${tag}>${escapeXml(val)}</${tag}>`;
}
function renderXmlOpeningTag(node, record, special) {
    if (!node.attributes || node.attributes.length === 0) {
        return `<${node.name}>`;
    }
    const attrs = [];
    for (const attr of node.attributes) {
        const raw = attr.source === 'field'
            ? resolveValue(attr.sourceField, record, special)
            : attr.literal;
        if (raw === null || raw === undefined || raw === '')
            continue;
        attrs.push(`${attr.name}="${escapeXml(raw)}"`);
    }
    return attrs.length > 0 ? `<${node.name} ${attrs.join(' ')}>` : `<${node.name}>`;
}
function renderXmlNode(node, records, special, indent) {
    const fallbackRecord = records[0] ?? {};
    switch (node.type) {
        case 'field': {
            const record = records[0] ?? {};
            const val = resolveValue(node.sourceField ?? '', record, special);
            if (!node.attributes || node.attributes.length === 0) {
                return `${indent}${renderXmlValue(node.name, val)}\n`;
            }
            return `${indent}${renderXmlOpeningTag(node, record, special)}${escapeXml(val)}</${node.name}>\n`;
        }
        case 'group': {
            let out = `${indent}${renderXmlOpeningTag(node, fallbackRecord, special)}\n`;
            for (const child of node.children ?? []) {
                out += renderXmlNode(child, records, special, indent + '  ');
            }
            out += `${indent}</${node.name}>\n`;
            return out;
        }
        case 'collection': {
            let out = `${indent}${renderXmlOpeningTag(node, fallbackRecord, special)}\n`;
            for (const record of records) {
                for (const child of node.children ?? []) {
                    out += renderXmlNodeForRecord(child, record, special, indent + '  ');
                }
            }
            out += `${indent}</${node.name}>\n`;
            return out;
        }
    }
}
function renderXmlNodeForRecord(node, record, special, indent) {
    switch (node.type) {
        case 'field': {
            const val = resolveValue(node.sourceField ?? '', record, special);
            if (!node.attributes || node.attributes.length === 0) {
                return `${indent}${renderXmlValue(node.name, val)}\n`;
            }
            return `${indent}${renderXmlOpeningTag(node, record, special)}${escapeXml(val)}</${node.name}>\n`;
        }
        case 'group': {
            let out = `${indent}${renderXmlOpeningTag(node, record, special)}\n`;
            for (const child of node.children ?? []) {
                out += renderXmlNodeForRecord(child, record, special, indent + '  ');
            }
            out += `${indent}</${node.name}>\n`;
            return out;
        }
        case 'collection': {
            let out = `${indent}${renderXmlOpeningTag(node, record, special)}\n`;
            for (const child of node.children ?? []) {
                out += renderXmlNodeForRecord(child, record, special, indent + '  ');
            }
            out += `${indent}</${node.name}>\n`;
            return out;
        }
    }
}
// ---------------------------------------------------------------------------
// Public renderers
// ---------------------------------------------------------------------------
/**
 * Render a JSON export preview from mappings + sample data.
 *
 * Without structure: flat array of mapped records.
 * With structure: nested object following ExportStructureNode tree
 *   (groups → objects, collections → arrays, fields → values).
 */
export function renderJsonPreview(mappings, sampleData, structure, specByColumn) {
    const { records, special } = sampleData;
    if (structure) {
        // NOTE: specByColumn is NOT threaded through the structured-output path
        // (renderStructureNode → renderStructureNodeForRecord). Format specs are
        // therefore not applied in structured JSON previews. Follow-up task needed
        // to pass specByColumn into renderStructureNode and use it at each field node.
        const result = renderStructureNode(structure, records, special);
        return JSON.stringify(result, null, 2);
    }
    const rows = records.map((r) => mapRecord(mappings, r, special, specByColumn));
    return JSON.stringify(rows, null, 2);
}
/**
 * Render an XML export preview from mappings + sample data.
 *
 * Without structure: `<records><record>...</record></records>`
 * With structure: nested XML following the ExportStructureNode tree.
 */
export function renderXmlPreview(mappings, sampleData, structure, specByColumn) {
    const { records, special } = sampleData;
    if (structure) {
        // NOTE: specByColumn is NOT threaded through the structured XML path
        // (renderXmlNode → renderXmlNodeForRecord). Format specs are not applied
        // in structured XML previews. Follow-up task needed to pass specByColumn
        // into renderXmlNode and apply it at each field node.
        let out = '<?xml version="1.0" encoding="UTF-8"?>\n';
        out += renderXmlNode(structure, records, special, '');
        return out.trimEnd();
    }
    let out = '<?xml version="1.0" encoding="UTF-8"?>\n<records>\n';
    for (const record of records) {
        out += '  <record>\n';
        for (const m of mappings) {
            const raw = resolveValue(m.sourceField, record, special);
            // Per-mapping-row format overrides apply on the flat XML path. The
            // structure path is intentionally untouched today — preview-only,
            // production wiring is tracked in 56-followup-xml-attributes-production-wiring.
            const val = resolveMappingValueForPreview(m, raw, specByColumn?.get(m.targetColumn));
            out += `    ${renderXmlValue(m.targetColumn, val)}\n`;
        }
        out += '  </record>\n';
    }
    out += '</records>';
    return out;
}
/**
 * Render a CSV export preview from mappings + sample data.
 *
 * First row is the header (targetColumn names); subsequent rows are data.
 * Values containing commas or quotes are quoted per RFC 4180.
 */
export function renderCsvPreview(mappings, sampleData, specByColumn) {
    const { records, special } = sampleData;
    function csvCell(val) {
        const s = String(val);
        if (s.includes(',') || s.includes('"') || s.includes('\n')) {
            return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
    }
    const header = mappings.map((m) => m.targetColumn).join(',');
    const rows = records.map((record) => mappings
        .map((m) => csvCell(resolveMappingValueForPreview(m, resolveValue(m.sourceField, record, special), specByColumn?.get(m.targetColumn))))
        .join(','));
    return [header, ...rows].join('\n');
}
/**
 * Render a fixed-width export preview from mappings + sample data.
 *
 * Each field is padded (or truncated) to the width specified in `widths`.
 * Missing width entries default to 10.
 */
export function renderFixedWidthPreview(mappings, sampleData, widths, specByColumn) {
    const { records, special } = sampleData;
    const rows = records.map((record) => {
        return mappings
            .map((m) => {
            const raw = resolveValue(m.sourceField, record, special);
            const val = String(resolveMappingValueForPreview(m, raw, specByColumn?.get(m.targetColumn)));
            const w = widths[m.targetColumn] ?? 10;
            // Pad or truncate to width
            return val.length >= w ? val.slice(0, w) : val.padEnd(w, ' ');
        })
            .join('');
    });
    return rows.join('\n');
}
//# sourceMappingURL=preview-renderer.js.map